import { useState } from 'react';
import { useQuery } from '@apollo/react-hooks';

import FeatureGates from '@atlaskit/feature-gate-js-client';

import { isExpectedApolloError } from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql';

import type {
	ConvertPageToFolderQuery as ConvertPageToFolderQueryType,
	ConvertPageToFolderQueryVariables,
} from './__types__/ConvertPageToFolderQuery';
import { ConvertPageToFolderQuery } from './ConvertPageToFolderQuery.graphql';

export const useCanConvertPageToFolder = ({
	contentId,
	spaceKey,
	contentType,
	skipQuery,
}: {
	contentId: string;
	spaceKey: string;
	contentType: string;
	skipQuery?: boolean;
}) => {
	const [canConvertPageToFolder, setCanConvertPageToFolder] = useState<boolean>(false);

	const isFolderConversionEnabled = FeatureGates.checkGate(
		'confluence_enable_page_to_folder_conversion',
	);

	const { data, loading, error } = useQuery<
		ConvertPageToFolderQueryType,
		ConvertPageToFolderQueryVariables
	>(ConvertPageToFolderQuery, {
		skip: skipQuery || !isFolderConversionEnabled || contentType !== 'page',
		variables: { contentId, spaceKey },
		fetchPolicy: 'cache-and-network',
		onCompleted: (data) => {
			const isFoldersEnabled =
				data?.space?.spaceTypeSettings?.enabledContentTypes?.isFoldersEnabled;
			const contentNode = data?.content?.nodes?.[0];

			const canEdit =
				(contentNode?.operations?.filter(
					(operationCheckResult: any) =>
						operationCheckResult?.operation === 'update' &&
						operationCheckResult?.targetType === 'page',
				)?.length || 0) > 0;

			const isParent = (contentNode?.children?.count || 0) > 0;

			const storageValue = contentNode?.body?.storage?.value;

			/**
			 * Only allow folder conversion if page body is empty or has only page tree macro.
			 * This regex will match if the string is empty or there is exactly one <p> tag and the entire string
			 * is enclosed within a <p> tag that contains page tree macro. Ignores <p /> which are empty new lines.
			 * Ignores whitespace between <p /> tags and outside the string.
			 */
			const emptyOrPageTreeMacroRegex = new RegExp(
				'^\\s*(?!.*<p>.*<p>.*)(<p><ac:structured-macro ac:name="pagetree".*</p>|\\s*<p />)*\\s*$',
			);
			const emptyOrPageTreeMacro =
				storageValue != null && emptyOrPageTreeMacroRegex.test(storageValue);

			setCanConvertPageToFolder(
				(isFoldersEnabled && canEdit && isParent && emptyOrPageTreeMacro) || false,
			);
		},
		onError: (error) => {
			if (isExpectedApolloError(error)) {
				markErrorAsHandled(error);
			}
		},
	});

	const contentTitle = data?.content?.nodes?.[0]?.title;

	return { loading, error, canConvertPageToFolder, setCanConvertPageToFolder, contentTitle };
};
